import React, { useState, useCallback, useEffect } from 'react';
import Alert from '@amzn/meridian/alert';
import Toaster from '@amzn/meridian/toaster';
import PropTypes from 'prop-types';
import {
  SUBMIT_BUTTON_PROPERTIES,
  FEEDBACK_FORM_PROPERTIES,
  SIF_PROFILE_ID,
} from '../constants/FeedbackConstants';
import FeedBackIntent from '../enums/feedbackIntents';
import GeneralFeedback from './GeneralFeedback';
import TriggeredFeedback from './TriggeredFeedback';
import useResponse from '../hooks/useResponse';
import createUserResponseObject from '../utils/createUserResponseObject';
import useQuestions from '../hooks/useQuestions';
import useNotification from '../hooks/useNotification';
import { isSiteExternal } from '../utils/isSiteExternal';

const FeedbackForm = ({
  feedbackIntent,
  title,
  description,
  submitButtonSize,
  submitButtonType,
  submitButtonText,
  confirmationMessage,
  applicationId,
  feedbackSubmissionPageIdentifier,
  triggeredObj: { isTriggered, resetTrigger },
  userId,
  isSkippable,
  prePopulate,
  displayMode,
}) => {
  const isGeneralFeedback = feedbackIntent === FeedBackIntent.General;
  const [open, setOpen] = useState(false);
  const [startTime, setStartTime] = useState(0);
  const [disabled, setDisabled] = useState(true);
  const { response, submitResponse, initializeResponse } = useResponse();
  const { questions } = useQuestions();
  const { toasts, onCloseToast } = useNotification();

  // On first rendering set startTime to the current time if feedbackIntent is Triggered.
  useEffect(() => {
    if (!isGeneralFeedback) {
      setStartTime(Date.now());
    }
  }, []);

  const requiredNotAnswered = ({ isMandatory, answer }) => {
    return isMandatory && !answer?.toString().trim();
  };

  useEffect(() => {
    if (response.answers.some(requiredNotAnswered)) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [response]);

  const onClickLink = useCallback(() => {
    setOpen(true);
    setStartTime(Date.now()); // set startTime to current time when user clicks on submit feedback link.
  }, []);

  const onClose = () => {
    setOpen(false);
    resetTrigger();
    initializeResponse(questions); // reset questions
  };

  const sifProfileId = isSiteExternal() ? SIF_PROFILE_ID : undefined;

  const onClickFooterButton = useCallback(() => {
    const timeTakenToRespond = Date.now() - startTime; // total time taken to respond
    // user response object which will be stored in backend
    const { userFeedback } = createUserResponseObject(
      response,
      questions,
      timeTakenToRespond,
      feedbackSubmissionPageIdentifier,
      userId
    );
    submitResponse(applicationId, feedbackIntent, userFeedback, sifProfileId);
    onClose();
  });

  return (
    <>
      {feedbackIntent === FeedBackIntent.General ? (
        <GeneralFeedback
          open={open}
          isDisabled={disabled}
          onClose={onClose}
          onClickLink={onClickLink}
          onClickFooterButton={onClickFooterButton}
          questions={questions}
          prePopulate={prePopulate}
          title={title}
          description={description}
          submitButtonSize={submitButtonSize}
          submitButtonType={submitButtonType}
          submitButtonText={submitButtonText}
          displayMode={displayMode}
        />
      ) : (
        <TriggeredFeedback
          open={isTriggered}
          isDisabled={disabled}
          onClose={onClose}
          onClickFooterButton={onClickFooterButton}
          questions={questions}
          prePopulate={prePopulate}
          title={title}
          description={description}
          submitButtonSize={submitButtonSize}
          submitButtonType={submitButtonType}
          submitButtonText={submitButtonText}
          isSkippable={isSkippable}
        />
      )}

      <Toaster toasts={toasts} onCloseToast={onCloseToast}>
        {() => (
          <Alert toast type="success">
            {confirmationMessage}
          </Alert>
        )}
      </Toaster>
    </>
  );
};

FeedbackForm.propTypes = {
  feedbackIntent: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  confirmationMessage: PropTypes.string,
  submitButtonSize: PropTypes.string,
  submitButtonType: PropTypes.string,
  submitButtonText: PropTypes.string,
  applicationId: PropTypes.string.isRequired,
  feedbackSubmissionPageIdentifier: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  triggeredObj: PropTypes.shape({
    isTriggered: PropTypes.bool.isRequired,
    resetTrigger: PropTypes.func.isRequired,
  }).isRequired,
  userId: PropTypes.string,
  isSkippable: PropTypes.bool,
  prePopulate: PropTypes.object.isRequired,
};

FeedbackForm.defaultProps = {
  title: FEEDBACK_FORM_PROPERTIES.title,
  description: FEEDBACK_FORM_PROPERTIES.description,
  confirmationMessage: FEEDBACK_FORM_PROPERTIES.confirmationMessage,
  feedbackSubmissionPageIdentifier: null,
  submitButtonSize: SUBMIT_BUTTON_PROPERTIES.size,
  submitButtonType: SUBMIT_BUTTON_PROPERTIES.type,
  submitButtonText: SUBMIT_BUTTON_PROPERTIES.text,
  userId: null,
  isSkippable: true,
};

export default FeedbackForm;
